// HER DASHBOARD ITEM, SURFACE 3, CLAUSES 1 AND 2 — **THE STANDING ALERT: ONE ROW PER CONDITION,
// RAISED ONCE, UPDATED IN PLACE, AND ONLY EVER TAKEN OFF THE SCREEN BY GOING AWAY.**
//
// Her clause 1: *the failed-readings tile LEAVES Analytics.* Her clause 2: *the failure rows feed
// the alert surface, clearable, opening into the member / reading / cause detail with the heal
// action.* A1's own build note declares the carrier owed in as many words — *"the ONE ALERT THAT
// UPDATES IN PLACE (first seen / still present / current numbers) HAS NO CARRIER YET… the
// standing-alert store is A2's, because A2's door needs exactly the same row."* This is that row's
// law.
//
// **WHY A CARRIER HAD TO EXIST BEFORE THE TILE COULD MOVE, WHICH IS THE ORDER OF THE WHOLE ROUND.**
// Moving the failures tile off Analytics before there is somewhere for those rows to land removes
// the only place they are visible — which is *dashboards read green over failing members*, her own
// headline clause, broken by the round that serves it (R19). So: build the carrier, then move the
// tile onto it.
//
// **THE FLOOD A1 MEASURED IS WHAT THIS SHAPE IS AGAINST.** The patrol was writing a row a night and
// filing a ticket a night for a STANDING finding: 85 runs in 31 hours, 81 patrol tickets against 2
// real member tickets, and the two real ones buried. A1 closed it at the schedule; this closes it at
// the record. **A standing finding is ONE row that MOVES**, never a row per pass.
//
// IT IMPORTS NOTHING, so the server, the dashboard and a FAST fixture all load and RUN it.

// ---------------------------------------------------------------------------
// **THREE STATES, AND THE MIDDLE ONE IS THE WHOLE HARD CASE (R18).**
//
//   `open`         — raised, and she has not said she has seen it.
//   `acknowledged` — **she has seen it AND IT IS STILL HAPPENING.** It stays on her screen.
//   `resolved`     — the condition is gone. It leaves, for good, into the record.
//
// **>>> A CLEAR IS AN ACKNOWLEDGEMENT AND NOT A DELETION, AND THAT IS THE RULING THIS FILE TURNS
// ON. <<<** *She has seen it* and *it has gone away* are different facts, and **only the second may
// take an alert off her screen.** A tap that made an alert about failing members disappear while
// members were still failing is her own *never read green over failing members* clause, defeated by
// a control built to serve it — and it is the version anybody would ship, because "clearable" reads
// like "dismissable". What a clear really buys her is the thing she asked for: it comes off the
// count of what NEEDS her, and it stops shouting.
//
// **AND AN ACKNOWLEDGEMENT IS STAMPED AT THE NUMBERS SHE SAW.** She cleared *8 readings across 3
// members*; tonight it is 40 across 19. She has not seen that, so the acknowledgement is stale and
// the alert is OPEN again. Held as arithmetic below rather than as a promise here.
// ---------------------------------------------------------------------------
export const ALERT_OPEN = "open";
export const ALERT_ACKNOWLEDGED = "acknowledged";
export const ALERT_RESOLVED = "resolved";
export const ALERT_STATES = [ALERT_OPEN, ALERT_ACKNOWLEDGED, ALERT_RESOLVED];

/** The states that are drawn on her main screen. `resolved` is history and is not one of them. */
export const ALERT_ON_SCREEN = [ALERT_OPEN, ALERT_ACKNOWLEDGED];

// ---------------------------------------------------------------------------
// **A2's LAW, AS ARITHMETIC OVER THE ROSTER: AN ALERT SHE CANNOT ACT ON DOES NOT RENDER.**
//
// Her exact complaint about the Overview boxes is that they are DEAD — a number with nowhere to go.
// So a class declares the DETAIL it opens into, and `alertClassViolations` refuses one that declares
// none. That is the difference between a law and a comment: a class added next year with no detail
// reader behind it goes RED rather than rendering a figure with nothing under it.
//
// `heals` is separate from `detail` because they are different facts (R18): every alert opens into
// something, and only SOME of them have a remedy a tap can run. Reading one off the other would
// either offer a heal button that does nothing or hide the one that works.
// ---------------------------------------------------------------------------
export const ALERT_CLASSES = [
  {
    key: "generationFailures",
    // Her words, and byte-identical to the tile's own label, because the tile is the door and the
    // alert is what it opens onto — two names for one thing is how a member of this house learns to
    // distrust both (R28/R29).
    say: "Readings that failed",
    // What the row is a count OF, so the sentence composes without the surface knowing anything.
    one: "1 reading could not be delivered",
    many: "{n} readings could not be delivered",
    // The detail this alert opens into. Her own three words: member, reading, cause.
    detail: "failures",
    // **THE HEAL ACTION SPENDS AND SAYS SO.** It buys one geocode per member it tries, on a metered
    // vendor, so the surface must be able to tell her that before she taps rather than after.
    heals: true,
    healSays: "Try the remedy now",
    healCosts: "This asks an outside service about each member's birthplace.",
  },
];

export const ALERT_CLASS_KEYS = ALERT_CLASSES.map((c) => c.key);
export const alertClass = (key) => ALERT_CLASSES.find((c) => c.key === String(key || "")) || null;

/**
 * The roster's own three shapes, asked as one question a fixture drives.
 *
 * A class with no detail is A2's dead box; a class with no sentence is a number with no noun; a
 * `heals` flag with no words beside it is a button she cannot be told the cost of.
 */
export function alertClassViolations() {
  const bad = [];
  const seen = new Set();
  for (const c of ALERT_CLASSES) {
    const k = String(c.key || "");
    if (!k) bad.push("an alert class has no key");
    if (seen.has(k)) bad.push(`the ${k} class is declared twice`);
    seen.add(k);
    if (!c.detail) bad.push(`the ${k} alert opens onto nothing`);
    if (!c.say) bad.push(`the ${k} alert has no name she reads`);
    if (!c.one || !c.many) bad.push(`the ${k} alert cannot say what its number counts`);
    if (c.heals && (!c.healSays || !c.healCosts)) {
      bad.push(`the ${k} alert offers a remedy without saying what it costs`);
    }
  }
  return bad;
}

// ---------------------------------------------------------------------------
// **WHAT A PASS DOES WITH ONE CLASS.** Five answers, and the fifth is the one that keeps this
// honest.
// ---------------------------------------------------------------------------
export const ALERT_RAISE = "raise";
export const ALERT_UPDATE = "update";
export const ALERT_RESOLVE = "resolve";
export const ALERT_NONE = "none";
export const ALERT_HOLD = "hold";

/**
 * A measurement, or the absence of one.
 *
 * **`measured === null` IS *THE MEASUREMENT DID NOT HAPPEN*, AND IT MAY NEVER RESOLVE AN ALERT.**
 * A check that could not run and a condition that went away are different facts (ITEM 15's own
 * `unknown` state, one layer up) — and only one of them is good news. A pass that resolved on an
 * unreadable reading would take a live alert about failing members off her screen on the night the
 * database was busy, silently, in the flattering direction (R8-4/R19).
 *
 * A count that is not a real number is NOT a zero either. `Number(true)` is 1 and `Number("")` is 0
 * (R136/R137), and a coerced zero here reads as *the problem went away*.
 */
export function alertAction({ existing = null, measured = null } = {}) {
  const live = existing && typeof existing === "object" && !existing.resolvedAt ? existing : null;
  if (measured === null || measured === undefined || typeof measured !== "object") {
    return ALERT_HOLD;
  }
  const n = Number(measured.count);
  if (typeof measured.count !== "number" || !Number.isFinite(n)) return ALERT_HOLD;
  const count = Math.max(0, Math.trunc(n));
  if (count > 0) return live ? ALERT_UPDATE : ALERT_RAISE;
  return live ? ALERT_RESOLVE : ALERT_NONE;
}

/**
 * Which of the three states a stored row is in RIGHT NOW.
 *
 * The comparison is `count > clearedCount`, so an alert that has SHRUNK since she cleared it stays
 * acknowledged — she has already seen worse, and re-shouting at her because a number moved down is
 * how a screen teaches somebody to stop reading it. An alert with no clear stamp is open, whatever
 * the counts say.
 */
export function alertVisibleState(row) {
  if (!row || typeof row !== "object") return null;
  if (row.resolvedAt) return ALERT_RESOLVED;
  if (!row.clearedAt) return ALERT_OPEN;
  const count = Math.max(0, Math.trunc(Number(row.count) || 0));
  const seen = Math.max(0, Math.trunc(Number(row.clearedCount) || 0));
  return count > seen ? ALERT_OPEN : ALERT_ACKNOWLEDGED;
}

/** What one alert says, in her words, off the class's own nouns. */
export function alertSentence(row) {
  const c = alertClass(row && row.class);
  if (!c) return "";
  const count = Math.max(0, Math.trunc(Number(row && row.count) || 0));
  const members = Math.max(0, Math.trunc(Number(row && row.members) || 0));
  const head = count === 1 ? c.one : c.many.replace("{n}", count.toLocaleString("en-US"));
  if (!members) return `${head}.`;
  return `${head}, across ${members.toLocaleString("en-US")} ${members === 1 ? "member" : "members"}.`;
}

/**
 * The rows her surface draws.
 *
 * `null` for a payload that has NOT ARRIVED (the screen holds still); an array for one that has.
 * **Resolved rows are never in it** — that is the only thing that takes an alert off this screen —
 * and **a class nobody rosters is dropped**, because a row whose sentence this house cannot compose
 * would render as a blank line with a number beside it.
 *
 * Open first, then acknowledged; newest first inside each, which is her own standing order on
 * surface 1 (*newest first by date*).
 */
export function alertRows(rows, { states = ALERT_ON_SCREEN } = {}) {
  if (!Array.isArray(rows)) return null;
  const order = { [ALERT_OPEN]: 0, [ALERT_ACKNOWLEDGED]: 1, [ALERT_RESOLVED]: 2 };
  // **THE ARCHIVE ASKS THIS SAME MAPPER FOR A DIFFERENT SET RATHER THAN GETTING ONE OF ITS OWN**
  // (R129: one comparator, one call site). A second composer for the record would be a second answer
  // to what an alert SAYS, and the two would disagree the first time a class grew a noun. The
  // DEFAULT is the live screen's set, so a caller that says nothing can never be handed a resolved
  // row to draw as though it were still happening.
  const want = Array.isArray(states) && states.length ? states : ALERT_ON_SCREEN;
  return rows
    .map((r) => {
      const c = alertClass(r && r.class);
      const state = alertVisibleState(r);
      if (!c || !state || !want.includes(state)) return null;
      return {
        id: String((r && r.id) || ""),
        key: c.key,
        say: c.say,
        state,
        text: alertSentence(r),
        detail: c.detail,
        heals: Boolean(c.heals),
        healSays: c.heals ? c.healSays : "",
        healCosts: c.heals ? c.healCosts : "",
        count: Math.max(0, Math.trunc(Number(r && r.count) || 0)),
        members: Math.max(0, Math.trunc(Number(r && r.members) || 0)),
        firstSeen: String((r && r.firstSeenAt) || ""),
        lastSeen: String((r && r.lastSeenAt) || ""),
        resolved: String((r && r.resolvedAt) || ""),
      };
    })
    .filter(Boolean)
    .sort((a, b) => (order[a.state] ?? 9) - (order[b.state] ?? 9)
      || String(b.lastSeen).localeCompare(String(a.lastSeen))
      || a.key.localeCompare(b.key));
}

/**
 * How many alerts NEED her. Acknowledged ones do not — that is the whole of what a clear buys.
 * `null` travels through, so a caller cannot read *the payload has not arrived* as *nothing needs
 * you* (R18/R65, the same distinction `needsYouLines` is built around one roster over).
 */
export function alertNeedsCount(rows) {
  const drawn = alertRows(rows);
  if (drawn === null) return null;
  return drawn.filter((r) => r.state === ALERT_OPEN).length;
}

// ---------------------------------------------------------------------------
// THE SENTENCES. **THREE STATES, NOT TWO**, for the reason every other surface in this house states:
// a list that could not be READ says so, and never *nothing is wrong*.
// ---------------------------------------------------------------------------
export const ALERTS_UNREAD = "Your alerts could not be read just now.";
export const ALERTS_CLEAR = "Nothing is going wrong right now.";
/** What an alert she has already seen is labelled, so an acknowledged row is not read as new. */
export const ALERT_SEEN_NOTE = "You have seen this. It is still happening.";
/** The control, in her words. It never says "dismiss" — it does not dismiss anything. */
export const ALERT_CLEAR_LABEL = "Mark as seen";

/**
 * The one line above the list.
 *
 *   `undefined` — in flight. Holds still.
 *   `null`      — arrived and could not be read. Its own sentence.
 *   `[]`        — an ANSWER, and a good one.
 */
export function alertLine(rows) {
  if (rows === undefined) return null;
  if (rows === null) return ALERTS_UNREAD;
  const drawn = alertRows(rows);
  if (drawn === null) return ALERTS_UNREAD;
  const open = drawn.filter((r) => r.state === ALERT_OPEN).length;
  const seen = drawn.length - open;
  if (!drawn.length) return ALERTS_CLEAR;
  const parts = [];
  if (open) parts.push(`${open} ${open === 1 ? "thing needs" : "things need"} you`);
  if (seen) parts.push(`${seen} ${seen === 1 ? "is" : "are"} still happening after you saw it`);
  return `${parts.join(", and ")}.`;
}
